import React, { useState, useRef, useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import styled from "styled-components";
import { API_ENDPOINTS } from "../config/api";

import PageHeader from "../components/PageHeader";
import { Label } from "../components/Form";
import { Card } from "../components/Card";

const ImageUploadContainer = styled.div`
  background: #f8f9fa;
  min-height: 100vh;
  padding-top: 80px;
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled.div`
  flex: 1;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const AnalysisResult = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Explanation = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  line-height: 1.4;
`;

const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const ImagePreview = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
`;

const AnalyzingSpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AnalyzingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-top: 2px solid #333;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImageCard = styled(Card)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  overflow: hidden;
  padding: 0 0 0.125rem 0;
`;

const ImageInfo = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 4rem;
`;

const ResponseStatus = styled.span`
  color: white;
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;

  ${({ $status }) =>
    $status === "ALLOWED"
      ? `
    background: rgb(10, 184, 10);
  `
      : $status === "DISALLOWED"
      ? `
    background: rgb(218, 25, 41);
  `
      : `
    background: #6c757d;
  `}
`;

function ImageUpload({ user }) {
  const { botId: urlBotId } = useParams();
  const [images, setImages] = useState([]);
  const [botId, setBotId] = useState(urlBotId || "");
  const [botName, setBotName] = useState("");
  const [status, setStatus] = useState(null);
  const [analysisResults, setAnalysisResults] = useState({});
  const [analyzingImages, setAnalyzingImages] = useState({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(!!urlBotId);
  const fileInputRef = useRef(null);

  // Fetch bot name and check access when botId changes
  useEffect(() => {
    const fetchBotName = async () => {
      if (!botId) {
        setBotName("");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${API_ENDPOINTS.bot(botId)}?username=${encodeURIComponent(
            user?.username || ""
          )}`
        );
        if (response.ok) {
          const data = await response.json();
          if (data.bot) {
            setBotName(data.bot.name);
          } else {
            setBotName("");
          }
        } else if (response.status === 403) {
          // User doesn't have access to this bot
          setAccessDenied(true);
        } else {
          setBotName("");
        }
      } catch (error) {
        console.error("Error fetching bot name:", error);
        setBotName("");
      } finally {
        setLoading(false);
      }
    };

    fetchBotName();
  }, [user, botId]);

  // Redirect to dashboard if access is denied
  if (accessDenied) {
    return <Navigate to="/dashboard" />;
  }

  // Show loading state while checking access
  if (loading) {
    return null;
  }

  const handleBack = () => {
    window.history.back();
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length === 0) {
      setStatus({ type: "error", message: "Please select valid image files." });
      return;
    }

    const newImages = imageFiles.map((file) => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      preview: URL.createObjectURL(file)
    }));

    setImages((prev) => [...prev, ...newImages]);
    // Automatically analyze each new image
    newImages.forEach((image) => {
      setTimeout(() => sendImageToModel(image), 100); // Small delay to ensure state is updated
    });
  };

  const handlePageDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handlePageDragLeave = (event) => {
    event.preventDefault();
    // Only set drag over to false if we're leaving the page entirely
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handlePageDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);

    const files = Array.from(event.dataTransfer.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length === 0) {
      setStatus({ type: "error", message: "Please drop valid image files." });
      return;
    }

    const newImages = imageFiles.map((file) => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      preview: URL.createObjectURL(file)
    }));

    setImages((prev) => [...prev, ...newImages]);

    // Automatically analyze each new image
    newImages.forEach((image) => {
      setTimeout(() => sendImageToModel(image), 100); // Small delay to ensure state is updated
    });
  };

  const sendImageToModel = async (image) => {
    if (!botId) {
      setStatus({
        type: "error",
        message: "Please select a bot first."
      });
      return;
    }

    // Set individual image as analyzing
    setAnalyzingImages((prev) => ({
      ...prev,
      [image.id]: true
    }));

    try {
      const formData = new FormData();
      formData.append("image", image.file);
      formData.append("botId", botId);

      const response = await fetch(API_ENDPOINTS.analyzeImage, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`
        }
      });

      if (!response.ok) {
        throw new Error("Failed to send image to model");
      }

      const result = await response.json();

      // Save the analysis result
      setAnalysisResults((prev) => ({
        ...prev,
        [image.id]: result
      }));

      console.log("Analysis result:", result);
    } catch (error) {
      console.error("Error sending image:", error);
      setStatus({
        type: "error",
        message: `Failed to analyze ${image.name}: ${error.message}`
      });
    } finally {
      // Clear individual image analyzing state
      setAnalyzingImages((prev) => ({
        ...prev,
        [image.id]: false
      }));
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <ImageUploadContainer
      onDragOver={handlePageDragOver}
      onDragLeave={handlePageDragLeave}
      onDrop={handlePageDrop}
      style={{
        position: "relative",
        backgroundColor: isDragOver ? "rgba(0, 0, 0, 0.1)" : "transparent",
        transition: "background-color 0.2s ease"
      }}
    >
      {isDragOver && (
        <div
          style={{
            position: "absolute",
            top: "80px",
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            pointerEvents: "none"
          }}
        >
          <Label>Drop images to analyze</Label>
        </div>
      )}
      <PageHeader
        user={user}
        showBackButton={true}
        onBack={handleBack}
        title={urlBotId && botName}
        rightActions={[
          {
            label: "Upload",
            onClick: handleUploadClick
          }
        ]}
        successMessage={
          status?.type === "success" || status?.type === "info"
            ? status.message
            : null
        }
        errorMessage={status?.type === "error" ? status.message : null}
      />

      <ContentContainer>
        <HiddenFileInput
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
        />

        {images.length === 0 && !isDragOver && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "calc(100vh - 80px - 4rem)",
              width: "100%"
            }}
          >
            <Label>Drag and drop images here</Label>
          </div>
        )}

        {images.length > 0 && (
          <ImagesGrid>
            {images.map((image) => (
              <ImageCard key={image.id} className="image-card">
                <ImagePreview>
                  <Image src={image.preview} alt={image.name} />
                </ImagePreview>
                <ImageInfo>
                  {analyzingImages[image.id] && (
                    <AnalyzingSpinnerContainer>
                      <AnalyzingSpinner />
                      Analyzing...
                    </AnalyzingSpinnerContainer>
                  )}
                  {analysisResults[image.id] && (
                    <AnalysisResult>
                      {(() => {
                        const response = analysisResults[image.id].response;
                        const parts = response.split(" --- ");
                        const status = parts[0]?.toUpperCase();
                        const explanation = parts[1] || "";

                        return (
                          <>
                            <ResponseStatus $status={status}>
                              {status}
                            </ResponseStatus>
                            {explanation && (
                              <Explanation>{explanation}</Explanation>
                            )}
                          </>
                        );
                      })()}
                    </AnalysisResult>
                  )}
                </ImageInfo>
              </ImageCard>
            ))}
          </ImagesGrid>
        )}
      </ContentContainer>
    </ImageUploadContainer>
  );
}

export default ImageUpload;
